import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { api } from "@/lib/api";
import { extractIntakeFields } from "@/lib/ai";
import { ArrowRight, ArrowLeft, Check, Scale, Upload, Sparkles, FileWarning } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Στοιχεία υπόθεσης", "Εντολέας & κατηγορίες", "Δικαστήριο & δικάσιμος", "Ολοκλήρωση"];

export default function Onboarding() {
  const { t, setCurrentCaseId, loadCases } = useApp();
  const navigate = useNavigate();
  // "upload" (πρώτη οθόνη — intake εγγράφου) → "form" (ο κανονικός 4-step wizard)
  const [stage, setStage] = useState("upload");
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [intakeWarning, setIntakeWarning] = useState(null);
  const fileRef = useRef(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    title: "", case_number: "", client_name: "",
    charges: "", court: "", prosecutor: "", next_hearing_date: "",
    factual_summary: "", stage: "Προκαταρκτική",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleIntakeFile = async (file) => {
    if (!file) return;
    setIntakeLoading(true);
    setIntakeWarning(null);
    try {
      const result = await extractIntakeFields(file);
      if (!result.ok) {
        setIntakeWarning(result.message || "Αποτυχία αναγνώρισης εγγράφου.");
        return;
      }
      const f = result.fields || {};
      setForm(prev => ({
        ...prev,
        title: f.title || prev.title,
        case_number: f.case_number || prev.case_number,
        client_name: f.client_name || prev.client_name,
        court: f.court || prev.court,
        prosecutor: f.prosecutor || prev.prosecutor,
        charges: (f.charges && f.charges.length) ? f.charges.join(", ") : prev.charges,
        factual_summary: f.legal_issues
          ? `${f.factual_summary || ""}\n\nΝομικά ζητήματα: ${f.legal_issues}`.trim()
          : (f.factual_summary || prev.factual_summary),
      }));
      toast.success(`Αναγνωρίστηκαν στοιχεία υπόθεσης (ακρίβεια OCR ${result.confidence ?? "—"}%)`);
      setStage("form");
    } catch (e) {
      setIntakeWarning(e?.response?.data?.detail || "Σφάλμα κατά την επεξεργασία του εγγράφου.");
    } finally {
      setIntakeLoading(false);
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        charges: form.charges.split(",").map(s => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/cases", payload);
      await loadCases();
      setCurrentCaseId(data.id);
      toast.success("Η υπόθεση δημιουργήθηκε");
      navigate("/");
    } catch (e) {
      toast.error("Σφάλμα δημιουργίας υπόθεσης");
    } finally { setLoading(false); }
  };

  const fields = [
    [
      { k: "title", label: "Τίτλος υπόθεσης", ph: "π.χ. Ποινική υπόθεση — Παπαδόπουλος" },
      { k: "case_number", label: "Αριθμός δικογραφίας", ph: "π.χ. ΠΟΙΝ. 1284/2026" },
    ],
    [
      { k: "client_name", label: "Όνομα εντολέα", ph: "π.χ. Ιωάννης Παπαδόπουλος" },
      { k: "charges", label: "Κατηγορίες (διαχωρισμός με κόμμα)", ph: "π.χ. Σωματική βλάβη, Διατάραξη ησυχίας" },
    ],
    [
      { k: "court", label: "Δικαστήριο", ph: "π.χ. Μονομελές Πλημμελειοδικείο Αθηνών" },
      { k: "prosecutor", label: "Εισαγγελική αρχή", ph: "π.χ. Εισαγγελία Πλημ. Αθηνών" },
      { k: "next_hearing_date", label: "Ημερομηνία επόμενης δικασίμου", type: "date" },
    ],
    [
      { k: "factual_summary", label: "Σύνοψη πραγματικών (προαιρετικά)", textarea: true, ph: "Σύντομη περιγραφή της υπόθεσης..." },
    ],
  ][step];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <Scale className="size-7 gold-text"/>
          <span className="font-heading font-bold text-lg">ΙΧΝΟΔΙΚΗΣ</span>
        </div>

        {stage === "upload" ? (
          <div className="surface-card p-8 lg:p-10" data-testid="onboarding-intake-card">
            <div className="font-mono text-[10.5px] gold-text uppercase tracking-[0.25em] mb-3">
              Καταχώρηση νέας υπόθεσης
            </div>
            <h2 className="font-display text-3xl font-semibold mb-1.5">Ανέβασμα εγγράφου</h2>
            <p className="text-muted-foreground text-[13px] mb-7">
              Ανεβάστε ένα δικόγραφο/έγγραφο της υπόθεσης — τα βασικά στοιχεία (τίτλος,
              αριθμός δικογραφίας, εντολέας, δικαστήριο, κατηγορίες) θα συμπληρωθούν
              αυτόματα μέσω OCR + AI, προς επιθεώρηση στα επόμενα βήματα.
            </p>

            <div
              data-testid="onboarding-intake-dropzone"
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleIntakeFile(e.dataTransfer.files?.[0]); }}
              onClick={() => !intakeLoading && fileRef.current?.click()}
              className="border-2 border-dashed border-border/60 rounded-lg p-10 text-center cursor-pointer hover:border-[hsl(var(--gold)/0.5)] transition-colors"
            >
              {intakeLoading ? (
                <>
                  <Sparkles className="size-7 mx-auto mb-3 gold-text animate-pulse-soft"/>
                  <div className="text-[13px] font-medium">Ανάγνωση εγγράφου (OCR) & εξαγωγή στοιχείων…</div>
                </>
              ) : (
                <>
                  <Upload className="size-7 mx-auto mb-3 text-muted-foreground"/>
                  <div className="text-[13px] font-medium">Drag & drop ή κάντε κλικ</div>
                  <div className="text-[11.5px] text-muted-foreground mt-1">PDF ή εικόνα (σάρωση/φωτογραφία δικογράφου)</div>
                </>
              )}
              <input ref={fileRef} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif,.webp,.bmp"
                onChange={e => handleIntakeFile(e.target.files?.[0])}/>
            </div>

            {intakeWarning && (
              <div className="mt-4 flex items-start gap-2 p-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-[12.5px]" data-testid="onboarding-intake-warning">
                <FileWarning className="size-4 text-amber-500 shrink-0 mt-0.5"/>
                <div>
                  <div className="font-medium">{intakeWarning}</div>
                  <div className="text-muted-foreground mt-0.5">Δοκιμάστε ξανά με καθαρότερη φωτογραφία/σάρωση.</div>
                </div>
              </div>
            )}

            <div className="flex justify-end mt-8">
              <button
                data-testid="onboarding-intake-skip"
                onClick={() => setStage("form")}
                className="flex items-center gap-1.5 px-4 py-2 text-[13px] text-muted-foreground hover:text-foreground"
              >Παράλειψη — χειρόγραφη καταχώρηση<ArrowRight className="size-4"/></button>
            </div>
          </div>
        ) : (
        <div className="surface-card p-8 lg:p-10" data-testid="onboarding-card">
          <div className="font-mono text-[10.5px] gold-text uppercase tracking-[0.25em] mb-3">
            {t("step_of", { n: step + 1, total: STEPS.length })}
          </div>
          <h2 className="font-display text-3xl font-semibold mb-1.5">{STEPS[step]}</h2>
          <p className="text-muted-foreground text-[13px] mb-7">{t("onboarding_title")}</p>

          <div className="flex gap-1 mb-8">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-[hsl(var(--gold))]" : "bg-border"}`}/>
            ))}
          </div>

          <div className="space-y-4">
            {fields.map(f => (
              <div key={f.k}>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{f.label}</label>
                {f.textarea ? (
                  <textarea
                    data-testid={`onboarding-${f.k}`}
                    value={form[f.k]} onChange={e => update(f.k, e.target.value)}
                    placeholder={f.ph}
                    rows={5}
                    className="w-full mt-1.5 bg-muted/30 border border-border/80 rounded-md px-3 py-2.5 text-[13.5px] outline-none focus:border-[hsl(var(--gold)/0.5)] resize-none"
                  />
                ) : (
                  <input
                    data-testid={`onboarding-${f.k}`}
                    type={f.type || "text"}
                    value={form[f.k]} onChange={e => update(f.k, e.target.value)}
                    placeholder={f.ph}
                    className="w-full mt-1.5 bg-muted/30 border border-border/80 rounded-md px-3 py-2.5 text-[13.5px] outline-none focus:border-[hsl(var(--gold)/0.5)]"
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-8">
            <button
              data-testid="onboarding-back"
              disabled={step === 0}
              onClick={() => setStep(s => s - 1)}
              className="flex items-center gap-1.5 px-3 py-2 text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-30"
            ><ArrowLeft className="size-4"/>{t("back")}</button>
            {step < STEPS.length - 1 ? (
              <button
                data-testid="onboarding-next"
                onClick={() => setStep(s => s + 1)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13px]"
              >{t("next")}<ArrowRight className="size-4"/></button>
            ) : (
              <button
                data-testid="onboarding-finish"
                disabled={loading || !form.title}
                onClick={submit}
                className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13px] disabled:opacity-60"
              >{loading ? t("loading") : t("finish")}<Check className="size-4"/></button>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
